import { Bold, Italic, Link, List, ListOrdered, Underline } from "lucide-react";
import * as S from "./styles.ts";
import { Children, ReactNode, useEffect, useState } from "react";
import { createEditor, Editor, Transforms } from "slate";
import { Editable, Slate, useSlate, withReact } from "slate-react";

const slateBlockTypes = ["paragraph", "ul", "ol"];

export const formattingOptions = [
  {
    key: "bold",
    icon: <Bold width={16} />,
    label: "Bold",
    render: (children: any) => <S.Bold>{children}</S.Bold>,
  },
  {
    key: "italic",
    icon: <Italic width={16} />,
    label: "Italic",
    render: (children: any) => <S.Italic>{children}</S.Italic>,
  },
  {
    key: "underline",
    icon: <Underline width={16} />,
    label: "Underline",
    render: (children: any) => <S.Underline>{children}</S.Underline>,
  },
  {
    key: "link",
    icon: <Link size={16} />,
    label: "Link",
    render: (
      children: any,
      {
        url,
        title,
      }: {
        url?: string;
        title?: string;
      }
    ) => (
      <S.Link
        href={url}
        title={title}
      >
        {children}
      </S.Link>
    ),
  },
];

export const blockOptions = [
  {
    key: "ol",
    icon: <ListOrdered width={16} />,
    label: "Numbered List",
    render: (children: any) => (
      <S.OrderedList>
        {Children.map(children, (c) => (
          <li key={c.key}>{c}</li>
        ))}
      </S.OrderedList>
    ),
  },
  {
    key: "ul",
    icon: <List width={16} />,
    label: "Bullet List",
    render: (children: any) => (
      <S.UnorderedList>
        {Children.map(children, (c) => (
          <li key={c.key}>{c}</li>
        ))}
      </S.UnorderedList>
    ),
  },
];

export const renderElement = (props: any) => {
  const { attributes, children, element } = props;

  const elementType = element.type;

  const blockOption = blockOptions.find((option) => option.key === elementType);

  if (blockOption) {
    return <span {...attributes}>{blockOption.render(children)}</span>;
  }

  return (
    <span {...attributes}>
      <S.P>{children}</S.P>
    </span>
  );
};

export const renderLeaf = (props: any) => {
  const { attributes, children, leaf } = props;

  const ogAttrs = Object.keys(leaf).filter((key) => key !== "text");

  const handleWrapper: any = (
    leafAttrs: string[],
    el?: any,
    {
      url,
      title,
    }: {
      url?: string;
      title?: string;
    } = {}
  ) => {
    if (leafAttrs.length > 0) {
      const wrapper = formattingOptions.find(
        (option) => option.key === leafAttrs[0],
      );

      if (wrapper) {
        return handleWrapper(
          leafAttrs.slice(1),
          wrapper.render(el, { url, title }),
        );
      }
    } else {
      return el;
    }
  };

  if (ogAttrs.includes("link")) {
    const { url = "", title = "", ...rest } = leaf;
    const restOgAttrs = Object.keys(rest).filter((key) => key !== "text");

    return (
      <span {...attributes}>
        {handleWrapper(restOgAttrs, children, {
          url,
          title,
        })}
      </span>
    );
  }

  if (Object.keys(ogAttrs).length) {
    return <span {...attributes}>{handleWrapper(ogAttrs, children)}</span>;
  }

  return <span {...attributes}>{children}</span>;
};
export const isBlockActive = (editor: any, format: any) => {
  const [match] = (Editor as any).nodes(editor, {
    match: (n: any) => n.type === format,
  });
  return !!match;
};

export const toggleBlock = (editor: any, format: any) => {
  const isActive = isBlockActive(editor, format);
  const isList = ["ol", "ul"].includes(format);

  (Transforms as any).unwrapNodes(editor, {
    match: (n: any) => ["ol", "ul"].includes(n.type),
    split: true,
  });

  (Transforms as any).setNodes(editor, {
    type: isActive ? "paragraph" : isList ? "list-item" : format,
  });

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const isMarkActive = (editor: any, format: any) => {
  const marks = (Editor as any).marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor: any, format: any) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const MarkdownWatcher = ({ markdown }: { markdown: any }) => {
  const editor = useSlate();

  useEffect(() => {
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });

    Transforms.removeNodes(editor, {
      at: [0],
    });

    Transforms.insertNodes(editor, markdown);
  }, [markdown]);

  return <></>;
};

const defaultValue = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];

export const RenderMarkdown = ({ children }: { children: any }) => {
  const [editor] = useState(() => withReact(createEditor()));
  return (
    <>
      <Slate
        editor={editor}
        initialValue={children || defaultValue}
      >
        <MarkdownWatcher markdown={children || defaultValue} />
        <Editable
          readOnly
          value={children}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          disabled
        />
      </Slate>
    </>
  );
};

const isSlateObject = (children: any) => {
  if (!children) return false;
  if (!Array.isArray(children)) return false;
  if (!children.length) return false;
  if (children.some((c) => typeof c === "string")) return false;
  return !children.some(
    (c) => typeof c === "object" && !slateBlockTypes.includes(c.type),
  );
};

export const Markdown = ({ children }: { children: ReactNode | any }) => {
  if (!isSlateObject(children)) {
    return children;
  }

  return <RenderMarkdown>{children}</RenderMarkdown>;
};
